import { get, isEmpty, size } from "lodash";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import cx from "classnames";
import { useNavigate } from "react-router-dom";

import Button from "../../components/button";
import Icons from "../../components/icons";
import { ICONS } from "../../constants/icons.constants";
import ThumbNail from "../../components/thumbNail/ThumbNail";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import NillDisplaySchedule from "../../components/nillDisplaySchedule/NillDisplaySchedule";
import { songActions } from "../../actions";
import favoriteSongStore from "../../zustand/favoriteSong.store";
import Table from "../../components/table/Table";
import { columns } from "../../config/homepage.config";

import styles from "./homepage.module.scss";
import { socketServices } from "../../services/socket.service";
import userStore from "../../zustand/user.store";
import imageStore from "../../zustand/image.store";
import {
  downloadPlaylistImage,
  downloadSongImage,
} from "../../helpers/generel.helpers";
import ignoredSongStore from "../../zustand/ignoreSong.store";
import { showToaster } from "../../helpers/toaster.helpers";

const Homepage = (prop, ref) => {
  const navigate = useNavigate();

  const { userOffline } = userStore((state) => ({
    userOffline: state.userOffline,
  }));

  const {
    currentSongIndex,
    // songs: musicPlayerSongs,
    playList,
    isPlaying,
    setIsPlaying,
    setCurrentSongIndex,
    searchedSong,
  } = musicPlayerStore((state) => ({
    currentSongIndex: state.currentSongIndex,
    playList: state.playList,
    isPlaying: state.isPlaying,
    setIsPlaying: state.setIsPlaying,
    setCurrentSongIndex: state.setCurrentSongIndex,
    searchedSong: state.searchedSong,
  }));
  const { songs: favouriteSongs, setFavouriteToSong } = favoriteSongStore(
    (state) => ({
      songs: state.songs,
      setFavouriteToSong: state.setFavouriteToSong,
    })
  );
  const { addImage } = imageStore((state) => ({
    addImage: state.addImage,
  }));
  const { songs: ignoredSongs } = ignoredSongStore((state) => ({
    songs: state.songs,
  }));

  const musicPlayerSongs = useMemo(() => {
    return playList?.songs || [];
  }, [playList]);

  const buttonRef = useRef(null);

  const handleSetCurrentSong = (song, index) => {
    const songId = song?.id;
    if (ignoredSongs[songId]) {
      return showToaster("info", "Ignored song can't be played");
    }
    setCurrentSongIndex(index);
  };

  const handlePlayClick = () => {
    setIsPlaying(!isPlaying);

    if (isPlaying) {
      socketServices.sendEvent(
        {
          type: "TOGGLE_PLAY_STATE",
          status: false,
        },
        userOffline
      );
    } else {
      socketServices.sendEvent(
        {
          type: "TOGGLE_PLAY_STATE",
          status: true,
        },
        userOffline
      );
    }
  };

  const handleLikeIconClick = async (song, favorite) => {
    const { id } = song;

    setFavouriteToSong(id);

    const isSuccess = await songActions.addFavouriteToSong(id, userOffline);

    if (!isSuccess) {
      setFavouriteToSong(id);
      return;
    }
  };

  const handleNewPlayListClick = () => {
    navigate("/my-playlist/new");
  };

  const downloadSongImages = useCallback(async () => {
    const songs = musicPlayerSongs || [];

    for (let index = 0; index < size(songs); index++) {
      if (index >= size(songs)) return;
      const imageToDownload = get(
        musicPlayerSongs,
        `${index}.thumbnail_img`,
        ""
      );

      try {
        const result = await downloadSongImage(imageToDownload);
        if (result) {
          addImage(imageToDownload);
        }
      } catch (error) {
        //
      }
    }
  }, [musicPlayerSongs, addImage]);

  const donwloadPlayListImage = useCallback(async () => {
    const image = playList?.thumbnail_img;
    const isSucess = await downloadPlaylistImage(image);
    if (isSucess) {
      addImage(image);
    }
  }, [addImage, playList?.thumbnail_img]);

  useEffect(() => {
    downloadSongImages();
    donwloadPlayListImage();
  }, [downloadSongImages, donwloadPlayListImage]);

  if (isEmpty(musicPlayerSongs))
    return (
      <div className={styles.page} style={{ display: "flex" }}>
        <NillDisplaySchedule
          head="You dont' have any songs in your playlist"
          btnPlaceholder="Create Playlist"
          iconType={ICONS.MY_PLAYLIST}
          onBtnClick={handleNewPlayListClick}
        />
      </div>
    );

  return (
    <div className={styles.page}>
      <div className={styles.first}>
        <div className={styles.topBox}>
          <div className={styles.imgbox}>
            <ThumbNail
              className={styles.thumbNail}
              name={
                playList
                  ? playList.name
                  : musicPlayerSongs[currentSongIndex]?.title
              }
              color={
                playList
                  ? playList.color
                  : musicPlayerSongs[currentSongIndex]?.color
              }
              hasImage={
                playList
                  ? !!playList?.thumbnail_img
                  : !!musicPlayerSongs[currentSongIndex]?.image
              }
              imgSrc={playList.thumbnail_img}
            />
          </div>
          <div className={styles.detailsBox}>
            <div className={styles.box1}>
              <p className={styles.firstLine}>
                {searchedSong
                  ? searchedSong.title
                  : musicPlayerSongs[currentSongIndex]?.title}
              </p>
              <div className={styles.likeBox}>
                <div
                  className={cx(styles.icon, {
                    [styles.active]:
                      favouriteSongs?.[musicPlayerSongs[currentSongIndex]?.id],
                  })}
                  onClick={() => {
                    if (searchedSong) {
                      handleLikeIconClick(
                        searchedSong,
                        favouriteSongs?.[searchedSong?.id]
                      );
                      return;
                    }
                    handleLikeIconClick(
                      musicPlayerSongs[currentSongIndex],
                      favouriteSongs?.[musicPlayerSongs[currentSongIndex]?.id]
                    );
                  }}
                >
                  <Icons type={ICONS.LIKE_SONG_LIST} />
                </div>

                <div className={styles.btn}>
                  <Button ref={buttonRef} onClick={handlePlayClick}>
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                </div>
              </div>
            </div>
            <div className={styles.secondLine}>
              {!playList ? (
                <p className={styles.artistName}>
                  {musicPlayerSongs[currentSongIndex]?.artist}
                </p>
              ) : (
                <div className={cx(styles.playlistName)}>
                  {playList.name || ""}

                  <div className={cx(styles.customTooltip)}>
                    {playList.name || ""}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.thirdLine}>
              <canvas
                style={{
                  width: "100%",
                  height: "100%",
                }}
                ref={ref}
              ></canvas>
            </div>
          </div>
        </div>
      </div>
      <Table
        rows={musicPlayerSongs}
        columns={columns}
        handleRowClick={(row, index) => {
          handleSetCurrentSong(row, index);
        }}
        className={styles.tableContainer}
      />
    </div>
  );
};

export default memo(forwardRef(Homepage));
